import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface CrisisRow {
  readonly crisisId: string;
  readonly crisisCode: string;
  readonly crisisTitle: string;
  readonly incidentClassification: string;
  readonly severity: string;
  readonly status: string;
  readonly declaredBy: string | null;
}

const CLASSIFICATIONS = ['', 'SYSTEM_FAILURE', 'APPLICATION_FAILURE', 'DATABASE_FAILURE', 'CLOUD_FAILURE', 'CYBER_INCIDENT', 'NETWORK_OUTAGE', 'DATA_CORRUPTION', 'HUMAN_ERROR', 'NATURAL_DISASTER', 'BUSINESS_DISRUPTION'];
const SEVERITIES = ['', 'LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const STATUSES = ['', 'DECLARED', 'TEAM_ACTIVATED', 'RECOVERY', 'ESCALATED', 'CLOSED'];
const DECLARE_CLASSIFICATIONS = CLASSIFICATIONS.filter(Boolean);
const DECLARE_SEVERITIES = SEVERITIES.filter(Boolean);

/**
 * M31 — Crisis Management Dashboard (§17 / §31). Register of declared crises by
 * incident classification and severity with their lifecycle status (DECLARED →
 * TEAM_ACTIVATED → RECOVERY → ESCALATED → CLOSED). Supports declaring a new
 * crisis and driving the response lifecycle — activate the crisis team, move to
 * recovery, escalate and close — each wired to the guarded /v1/bcm/crises write
 * API. Filterable by classification, severity and status.
 */
export function CrisisManagementPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<CrisisRow[]>([]);
  const [incidentClassification, setIncidentClassification] = useState('');
  const [severity, setSeverity] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newTitle, setNewTitle] = useState('');
  const [newClassification, setNewClassification] = useState('SYSTEM_FAILURE');
  const [newSeverity, setNewSeverity] = useState('HIGH');
  const [escalateTo, setEscalateTo] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (incidentClassification) params.set('incidentClassification', incidentClassification);
    if (severity) params.set('severity', severity);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: CrisisRow[] }>(`/v1/bcm/crises${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, incidentClassification, severity, status, reload]);

  const failMessage = (err: unknown): string =>
    err instanceof ApiError && err.status === 403
      ? t('m31.crisis.err403')
      : (err as Error).message;

  const declare = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newTitle.trim()) { setActionError(t('m31.crisis.errTitle')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/bcm/crises`, {
        method: 'POST',
        body: { crisisTitle: newTitle.trim(), incidentClassification: newClassification, severity: newSeverity },
        token,
      });
      setNotice(t('m31.crisis.noticeDeclared', { title: newTitle.trim() }));
      setNewTitle('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: CrisisRow, path: string, label: string, body?: unknown): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/bcm/crises/${r.crisisId}/${path}`, { method: 'POST', body: body ?? {}, token });
      setNotice(t('m31.crisis.actNotice', { code: r.crisisCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    }
  };

  const escalate = async (r: CrisisRow): Promise<void> => {
    const target = (escalateTo[r.crisisId] ?? '').trim();
    if (!target) { setActionError(t('m31.crisis.errEscalate')); return; }
    await act(r, 'escalate', t('m31.crisis.escalatedTo', { target }), { escalatedTo: target });
  };

  return (
    <main aria-label="crisis-management">
      <h1>{t('m31.crisis.title')}</h1>

      <section aria-label="declare-crisis">
        <h2>{t('m31.crisis.declareHeading')}</h2>
        <form aria-label="crisis-form" onSubmit={(e) => void declare(e)}>
          <label>{t('m31.crisis.fieldTitle')}<input aria-label="field-crisis-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} /></label>
          <label>{t('m31.crisis.fieldClassification')}
            <select aria-label="field-classification" value={newClassification} onChange={(e) => setNewClassification(e.target.value)}>
              {DECLARE_CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m31.crisis.fieldSeverity')}
            <select aria-label="field-severity" value={newSeverity} onChange={(e) => setNewSeverity(e.target.value)}>
              {DECLARE_SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-crisis" disabled={busy}>{busy ? t('m31.crisis.busyDeclare') : t('m31.crisis.submitDeclare')}</button>
        </form>
        {actionError && <p role="alert" aria-label="crisis-error">{actionError}</p>}
        {notice && <p aria-label="crisis-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m31.crisis.filterClassification')}
          <select aria-label="filter-classification" value={incidentClassification} onChange={(e) => setIncidentClassification(e.target.value)}>
            {CLASSIFICATIONS.map((c) => <option key={c || 'all'} value={c}>{c || t('m31.crisis.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m31.crisis.filterSeverity')}
          <select aria-label="filter-severity" value={severity} onChange={(e) => setSeverity(e.target.value)}>
            {SEVERITIES.map((s) => <option key={s || 'all'} value={s}>{s || t('m31.crisis.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m31.crisis.filterStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m31.crisis.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m31.crisis.loading')}</p> : (
        <>
          <p aria-label="crisis-count">{t('m31.crisis.count', { count: rows.length })}</p>
          <table aria-label="crisis-table">
            <thead><tr><th>{t('m31.crisis.thCode')}</th><th>{t('m31.crisis.thTitle')}</th><th>{t('m31.crisis.thClassification')}</th><th>{t('m31.crisis.thSeverity')}</th><th>{t('m31.crisis.thStatus')}</th><th>{t('m31.crisis.thActions')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.crisisId} data-severity={r.severity}>
                  <td>{r.crisisCode}</td><td>{r.crisisTitle}</td><td>{r.incidentClassification}</td>
                  <td>{r.severity}</td><td>{r.status}</td>
                  <td>
                    {r.status === 'DECLARED' && (
                      <button type="button" aria-label={`activate-team-${r.crisisCode}`} onClick={() => void act(r, 'activate-team', t('m31.crisis.teamActivated'))}>{t('m31.crisis.activateTeam')}</button>
                    )}
                    {r.status === 'TEAM_ACTIVATED' && (
                      <button type="button" aria-label={`recovery-${r.crisisCode}`} onClick={() => void act(r, 'recovery', t('m31.crisis.movedRecovery'))}>{t('m31.crisis.moveRecovery')}</button>
                    )}
                    {r.status !== 'CLOSED' && (
                      <>
                        <input aria-label={`escalate-input-${r.crisisCode}`} placeholder={t('m31.crisis.escalatePlaceholder')} value={escalateTo[r.crisisId] ?? ''} onChange={(e) => setEscalateTo((m) => ({ ...m, [r.crisisId]: e.target.value }))} />
                        <button type="button" aria-label={`escalate-${r.crisisCode}`} onClick={() => void escalate(r)}>{t('m31.crisis.escalate')}</button>
                        <button type="button" aria-label={`close-${r.crisisCode}`} onClick={() => void act(r, 'close', t('m31.crisis.closed'))}>{t('m31.crisis.close')}</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
